import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Ayuda } from '../components/Ayuda';
import { CargaPagina } from '../components/Spinner';
import ChatAndres from './ChatAndres';
import { CapitulosProyecto, AYUDA_CAPITULOS } from './dia/CapitulosProyecto';
import { FichaProyecto, AYUDA_FICHA } from './dia/FichaProyecto';
import type { Favorito } from './dia/tipos';

type Tab = 'chat' | 'ficha' | 'capitulos';
const TABS: { id: Tab; label: string; ayuda: string }[] = [
  { id: 'chat', label: 'Chat con Andrés', ayuda: 'Conversa con Andrés sobre ESTE proyecto: responde con los documentos de la carpeta, cita cada dato y puede redactar o corregir capítulos.' },
  { id: 'ficha', label: 'Ficha', ayuda: AYUDA_FICHA },
  { id: 'capitulos', label: 'Capítulos', ayuda: AYUDA_CAPITULOS },
];

/** Página de un proyecto DIA (2026-10-22): /agentes/dia/:favoritoId con pestañas Chat | Capítulos.
 *  (La pestaña Ficha llega en la Fase 2 del spec.) */
export default function ProyectoDia() {
  const { favoritoId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab: Tab = params.get('tab') === 'capitulos' ? 'capitulos' : params.get('tab') === 'ficha' ? 'ficha' : 'chat';
  const textoInicial = params.get('texto') ?? undefined;
  const [fav, setFav] = useState<Favorito | null | undefined>(undefined);
  useEffect(() => {
    apiFetch<{ favorito: Favorito }>(`/api/sharepoint/favoritos/${favoritoId}`)
      .then((d) => setFav(d.favorito))
      .catch(() => setFav(null));
  }, [favoritoId]);
  if (fav === undefined) return <CargaPagina mensaje="Abriendo el proyecto" />;
  if (fav === null) {
    return (
      <p role="alert" className="alerta-error">
        No encontramos este proyecto en tus DIAs. <Link to="/agentes/dia" className="underline">Volver al listado</Link>
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/agentes/dia" className="btn-fantasma !h-8 !px-3 !text-[13px]">← DIAs</Link>
        <h2 className="order-last basis-full truncate text-[15px] font-medium text-azul-noche sm:order-none sm:min-w-0 sm:flex-1 sm:basis-auto" title={fav.ruta}>★ {fav.nombre}</h2>
        <nav aria-label="Secciones del proyecto" className="flex rounded-xl bg-slate-100 p-0.5">
          {TABS.map((t) => (
            <Ayuda key={t.id} texto={t.ayuda} toque={false}>
              <button
                type="button"
                onClick={() => setParams(t.id === 'chat' ? {} : { tab: t.id })}
                aria-current={tab === t.id ? 'page' : undefined}
                className={`rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-colors ${tab === t.id ? 'bg-white text-azul-profundo shadow-sm' : 'text-gris hover:text-azul-noche'}`}
              >
                {t.label}
              </button>
            </Ayuda>
          ))}
        </nav>
      </div>
      {tab === 'chat' ? (
        <ChatAndres
          favoritoId={fav.id}
          nombre={fav.nombre}
          estudiando={fav.sync_estado === 'ingestando'}
          sinCapitulos
          textoInicial={textoInicial}
          onVolver={() => navigate('/agentes/dia')}
          claseAltura="h-[calc(100dvh-215px)] min-h-[420px] lg:h-[calc(100dvh-180px)]"
        />
      ) : tab === 'ficha' ? (
        <FichaProyecto favoritoId={fav.id} nombre={fav.nombre} onSincronizar={async () => { await apiFetch(`/api/sharepoint/favoritos/${fav.id}/ingestar`, { method: 'POST' }); }} />
      ) : (
        <CapitulosProyecto favoritoId={fav.id} />
      )}
    </div>
  );
}
